import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { STAGES } from "../lib/constants";
import { CalendarBlank } from "@phosphor-icons/react";

export function KanbanBoard({ engagements }) {
  const qc = useQueryClient();
  const [overCol, setOverCol] = useState(null);

  const move = useMutation({
    mutationFn: ({ id, stage }) => api.patch(`/engagements/${id}/stage`, { stage }),
    onSuccess: () => { toast.success("Stage updated"); qc.invalidateQueries({ queryKey: ["engagements"] }); },
    onError: (e) => { toast.error(errText(e)); qc.invalidateQueries({ queryKey: ["engagements"] }); },
  });

  const onDrop = (e, stage) => {
    e.preventDefault();
    setOverCol(null);
    const id = e.dataTransfer.getData("text/plain");
    if (id) move.mutate({ id, stage });
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 kanban-scroll" data-testid="kanban-board">
      {STAGES.map((s) => {
        const cards = (engagements || []).filter((e) => e.stage === s.key);
        return (
          <div key={s.key} data-testid={`kanban-col-${s.key}`}
            onDragOver={(e) => { e.preventDefault(); setOverCol(s.key); }}
            onDragLeave={() => setOverCol(null)}
            onDrop={(e) => onDrop(e, s.key)}
            className={`w-72 shrink-0 rounded-lg border ${overCol === s.key ? "border-zinc-400 bg-zinc-100" : "border-zinc-200 bg-zinc-50/50"} transition-colors duration-150`}>
            <div className="px-3 py-2.5 flex items-center justify-between border-b border-zinc-200">
              <span className="text-xs font-medium text-zinc-700">{s.label}</span>
              <span className="font-mono text-[10px] text-zinc-400 bg-white border border-zinc-200 rounded-sm px-1.5 py-0.5">{cards.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[120px]">
              {cards.map((e) => (
                <div key={e.id} draggable data-testid={`kanban-card-${e.id}`}
                  onDragStart={(ev) => ev.dataTransfer.setData("text/plain", e.id)}
                  className="rounded-md border border-zinc-200 bg-white p-3 cursor-grab active:cursor-grabbing hover:border-zinc-300 transition-colors duration-150">
                  <p className="text-sm font-medium text-zinc-900 leading-tight">{e.client_name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{e.service_type} · <span className="font-mono">{e.tax_year}</span></p>
                  {e.deadline && (
                    <p className="flex items-center gap-1 text-[11px] text-zinc-400 mt-2">
                      <CalendarBlank size={12} /> <span className="font-mono">{e.deadline}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
